import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Upload, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function AdminQuestions() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("all");
  const [open, setOpen] = useState(false);
  const [reviewFor, setReviewFor] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [review, setReview] = useState(null);
  const [form, setForm] = useState({
    exam_type: "LGS", subject: "Matematik", text: "", correct_option: "A",
    options: [{ key: "A", text: "" }, { key: "B", text: "" }, { key: "C", text: "" }, { key: "D", text: "" }],
    year: 2024, grade_level: "8", difficulty: "medium",
  });

  const load = () => {
    const p = type !== "all" ? `?exam_type=${type}` : "";
    api.get(`${V1}/admin/questions${p}`).then((r) => setItems(r.data || []));
  };
  useEffect(() => { load(); }, [type]);

  const create = async () => {
    try {
      await api.post(`${V1}/admin/questions`, form);
      toast.success("Soru eklendi");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eklenemedi"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`${V1}/admin/questions/${id}`);
    load();
  };

  const aiReview = async (q) => {
    setReviewFor(q);
    setReview(null);
    setReviewing(true);
    try {
      const { data } = await api.post(`${V1}/admin/questions/${q.id}/ai-review`);
      setReview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI incelemesi başarısız"); setReviewFor(null); }
    finally { setReviewing(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-questions-page">
      <div className="flex justify-between items-center">
        <div>
          <div className="overline text-accent">Soru Bankası</div>
          <h1 className="font-display text-3xl font-bold">Sorular</h1>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" data-testid="bulk-import-btn"><Link to="/admin/sorular/toplu-yukle"><Upload className="h-4 w-4" /> Toplu İçe Aktar</Link></Button>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="LGS">LGS</SelectItem>
              <SelectItem value="TYT">TYT</SelectItem>
              <SelectItem value="AYT">AYT</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-question-btn"><Plus className="h-4 w-4" /> Yeni Soru</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Soru Ekle</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div className="grid grid-cols-3 gap-2">
                  <div><Label>Sınav Türü</Label>
                    <Select value={form.exam_type} onValueChange={(v) => setForm({ ...form, exam_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="LGS">LGS</SelectItem><SelectItem value="TYT">TYT</SelectItem><SelectItem value="AYT">AYT</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>Ders</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
                  <div><Label>Yıl</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })} /></div>
                </div>
                <div><Label>Soru Metni</Label><Textarea rows={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="q-text" /></div>
                {form.options.map((o, i) => (
                  <div key={o.key} className="flex gap-2 items-center">
                    <span className="font-bold w-6">{o.key}.</span>
                    <Input value={o.text} onChange={(e) => {
                      const nxt = [...form.options]; nxt[i] = { ...o, text: e.target.value }; setForm({ ...form, options: nxt });
                    }} placeholder={`Seçenek ${o.key}`} />
                  </div>
                ))}
                <div><Label>Doğru Cevap</Label>
                  <Select value={form.correct_option} onValueChange={(v) => setForm({ ...form, correct_option: v })}>
                    <SelectTrigger data-testid="q-correct"><SelectValue /></SelectTrigger>
                    <SelectContent>{form.options.map((o) => <SelectItem key={o.key} value={o.key}>{o.key}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button><Button onClick={create} data-testid="save-question-btn">Kaydet</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="text-left p-3">Tip</th><th className="text-left p-3">Ders</th><th className="text-left p-3">Yıl</th><th className="text-left p-3">Soru</th><th className="text-left p-3">Cevap</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Soru yok.</td></tr>}
            {items.map((q) => (
              <tr key={q.id} className="border-t border-border">
                <td className="p-3"><span className="px-2 py-0.5 bg-muted rounded text-xs uppercase">{q.exam_type}</span></td>
                <td className="p-3">{q.subject}</td>
                <td className="p-3 font-mono">{q.year || "-"}</td>
                <td className="p-3 max-w-md truncate">{q.text}</td>
                <td className="p-3 font-bold">{q.correct_option}</td>
                <td className="p-3 flex items-center gap-2">
                  <button onClick={() => aiReview(q)} className="text-muted-foreground hover:text-accent" data-testid={`ai-review-btn-${q.id}`} title="AI ile incele">
                    <Sparkles className="h-4 w-4" />
                  </button>
                  <button onClick={() => del(q.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent className="max-w-lg" data-testid="ai-review-dialog">
          <DialogHeader>
            <DialogTitle>AI Soru İncelemesi</DialogTitle>
            <DialogDescription>AI&apos;nin bu soru üzerindeki danışma niteliğindeki ön incelemesi.</DialogDescription>
          </DialogHeader>
          {reviewing && <div className="text-sm text-muted-foreground py-6 text-center">İnceleniyor…</div>}
          {!reviewing && review && (
            <div className="space-y-3 text-sm">
              {review.quality_score != null && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">Kalite Skoru:</span>
                  <span className={`font-bold ${review.quality_score >= 70 ? "text-success" : review.quality_score >= 40 ? "text-amber-500" : "text-destructive"}`}>{review.quality_score}/100</span>
                </div>
              )}
              {review.duplicate_risk && (
                <div className="text-amber-600 text-xs">⚠ Bu soru mevcut bir soruyla benzer/tekrar olabilir.</div>
              )}
              {review.issues?.length > 0 && (
                <div>
                  <div className="font-medium mb-1">Tespit Edilen Noktalar:</div>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    {review.issues.map((i, idx) => <li key={idx}>{i}</li>)}
                  </ul>
                </div>
              )}
              {review.suggestion && (
                <div className="p-3 rounded-lg bg-accent/5 border border-accent/20">
                  <span className="font-medium">Öneri:</span> {review.suggestion}
                </div>
              )}
              <div className="text-xs text-muted-foreground italic">
                Not: Bu AI ön incelemesi danışma niteliğindedir; kesin doğru/yanlış kararı size aittir.
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setReviewFor(null)}>Kapat</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
